import { DragEvent, ReactNode } from "react";
import styles from "../../aic.module.css";
import { classMerge, EditorDefns } from "../../utility";
import { useArsContext } from "../contexts";
import { ArsSidebarItemKind } from "../sidebar/interface";
import { isArsSidebarItemData } from "../sidebar/typeguards/isArsSidebarItemData";
import { ArsBlockDropZone } from "./ArsBlockDropZone";
import { ArsBlockLine } from "./ArsBlockLine";
import { ArsPageByItem } from "./interface";

export interface ArsBlockProps<T> {
  disabled?: boolean;
  dropText?: T;
  editorDefns: EditorDefns<T>;
  id: string;
  setValue: (newValue: ArsPageByItem<T>[]) => void;
  style?: React.CSSProperties;
  value: ArsPageByItem<T>[];
}

/**
 * Drop-target block used by ARS PageBy editing.
 *
 * @param props - Component props
 * @param props.disabled - When true the block is read only
 * @param props.dropText - Text shown in the drop zone when no items are present; if not provided, a default message is shown
 * @param props.editorDefns - Editor defs used to create page-by labels
 * @param props.id - Stable id prefix for test hooks
 * @param props.setValue - Callback fired when the ordered items change
 * @param props.style - Optional style object applied to the block container
 * @param props.value - Ordered page-by items currently selected
 * @returns ARS page-by block UI
 */
export const ArsBlock = <T,>({
  disabled,
  dropText,
  editorDefns,
  id,
  setValue,
  style,
  value,
}: ArsBlockProps<T>): ReactNode => {
  const { state } = useArsContext();

  /**
   * Appends grouping factors dropped onto the block.
   *
   * @param event - Drop event from a grouping-factor drag source
   * @param ix - Index of the page-by item being dropped onto, used when multiple page-by blocks are present; currently unused as only one block is supported, but included for future extensibility
   */
  const addGroupingFactors = (event: DragEvent<HTMLDivElement>, ix: number) => {
    event.preventDefault();
    if (disabled) return;

    const transferText = event.dataTransfer.getData("application/json");
    if (!transferText) return;

    try {
      const parsed = JSON.parse(transferText) as { data?: unknown };
      if (!Array.isArray(parsed.data)) return;
      const newValue = [...value];
      const addValues: ArsPageByItem<T>[] = [];

      for (const item of parsed.data) {
        if (!isArsSidebarItemData(item) || item.kind !== ArsSidebarItemKind.GroupingFactor) {
          continue;
        }
        if (!state.groupingFactors[item.dropItem.id]) continue;
        if (
          newValue.some(({ groupingFactorId }) => groupingFactorId === item.dropItem.id) ||
          addValues.some(({ groupingFactorId }) => groupingFactorId === item.dropItem.id)
        )
          continue;
        addValues.push({
          groupingFactorId: item.dropItem.id,
          label: editorDefns.textToT(
            [item.dropItem.label, item.dropItem.name, item.dropItem.description, item.dropItem.id]
              .map((value) => value?.trim() ?? "")
              .find((value) => value !== "")!,
          ),
        });
      }
      newValue.splice(ix, 0, ...addValues);

      if (newValue.length !== value.length) {
        setValue(newValue);
      }
    } catch {
      return;
    }
  };

  return (
    <div
      id={id}
      className={styles.aibBlock}
      style={style}
    >
      {value.length === 0 ? (
        <div className={styles.aibLine}>
          <div className={styles.aibLineButtons} />
          <div className={styles.aibLineItemHolder}>
            <div
              className={styles.flexGrow1}
              style={{ position: "relative" }}
            >
              <ArsBlockDropZone
                id={`${id}-dropzone`}
                onDrop={(e) => addGroupingFactors(e, 0)}
                top="0"
                bottom="0"
              />
              <div className={classMerge([styles.aibLineItem, "fst-italic"])}>
                <editorDefns.Editor
                  id={`ars-block-line-editor-ro`}
                  editable={false}
                  value={dropText ?? editorDefns.textToT("Drop grouping factors here")}
                  setValue={
                    // istanbul ignore next
                    () => undefined
                  }
                />
              </div>
            </div>
          </div>
          <div className={styles.aibLineButtons} />
        </div>
      ) : (
        value.map((pageByItem, index) => (
          <div
            className={styles.aibLine}
            style={{ position: "relative" }}
            key={`${pageByItem.groupingFactorId}-${index}`}
          >
            {index === 0 && (
              <ArsBlockDropZone
                id={`${id}-dropzone-${index}`}
                onDrop={(e) => addGroupingFactors(e, 0)}
                top="-8px"
                bottom="calc(100% - 8px)"
              />
            )}
            <ArsBlockDropZone
              id={`${id}-dropzone-${index}`}
              onDrop={(e) => addGroupingFactors(e, index + 1)}
              top="calc(100% - 8px)"
              bottom="-8px"
            />
            <ArsBlockLine<T>
              disabled={disabled}
              editorDefns={editorDefns}
              pageByIndex={index}
              setValue={setValue}
              value={value}
            />
          </div>
        ))
      )}
    </div>
  );
};

ArsBlock.displayName = "ArsBlock";
